import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

interface OnTimePerformanceResponse {
  subcontractorId: string;
  onTimePerformance: number;
  rank: {
    rankText: string;
    color: string;
    arrowDirection: string;
  };
}

interface RevenueResponse {
  subcontractorId: string;
  totalRevenueCent: number;
  rank: {
    rankText: string;
    color: string;
    arrowDirection: string;
  };
}

interface MissedRevenueResponse {
  missed_earnings_cents_average: number;
  is_top_25_percent: boolean;
}

interface AverageTurnRateResponse {
  average_turn_rate: number;
  is_top_25_percent: boolean;
}

interface TotalRanLoadsResponse {
  total_ran_loads: number;
}

interface TotalRejectedLoadsResponse {
  total_rejected_loads: number;
}

interface UnassignedLoadsResponse {
  unassigned_loads: number;
}

interface LoadPerShiftResponse {
  load_per_shift: number;
}

interface DocumentationTardiness {
  average_upload_delay_status: string;
  user_name: string;
}

interface DocumentationTardinessResponse {
  documentation_tardiness: DocumentationTardiness[];
}

interface AppUsage {
  user_name: string;
  waypoint_frequent: number;
}

interface AppUsageResponse {
  app_usage: AppUsage[];
}

interface DriverRevenue {
  subcontractorId: string;
  driverId: string;
  driverName: string;
  totalRevenue: number;
  orderIds: null | number[];
  totalCost: number;
  totalProfit: number;
  cost: number;
  profit: number;
}

export interface LastWaypoint {
  latitude: number;
  longitude: number;
}

export interface DriverOnShift {
  shiftId: string;
  driverId: string;
  driverName: string;
  workingNights: boolean;
  shiftStartAtString: string;
  hosRemainingMinutes: number;
  minutesSinceLastCompletedLoad: number;
  lastWaypoint: LastWaypoint;
}

export interface DriverOnHitch {
  driverId: string;
  hitchId: string;
  driverName: string;
  hitchStartDate: string;
  hitchLastDate: string;
  workingNights: boolean;
  shiftStartAt: string;
  shiftEndAt: string;
  hitchStatus: string;
  shiftsRemaining: number;
  nextShiftStartDatetime: string;
  lastWaypoint: LastWaypoint;
}

export interface DriverInProgress {
  driverId: string;
  driverName: string;
  lastWaypoint: LastWaypoint;
  orderId: string;
  orderStatus: string;
  pickUp: string;
  dropOff: string;
  state: string;
}

interface DriverInProgressResponse {
  driver_in_progress: DriverInProgress[];
}

interface DriverOnHitchResponse {
  driverName: string;
  workingNights: boolean;
  hitchPeriod: string;
  currentStatus: string;
  shiftRemaining: number;
  nextShiftStartTime: string;
}

export interface UpdateMonthlyCostRequest {
  driverId: string;
  payType: 'fixed' | 'percentage';
  payAmountFixedCents: number | null;
  payAmountPercentage: string | null;
  insuranceCostCents: number;
  maintenanceCostCents: number;
}

export interface UpdateMonthlyCostResponse {
  success: boolean;
  updatedRecords: number;
}

export interface MappableDriver {
  driverId?: string;
  driverName: string;
  lastWaypoint?: LastWaypoint;
  status: 'in-progress' | 'on-shift' | 'on-hitch';

  orderId?: string;
  orderStatus?: string;
  pickUp?: string;
  dropOff?: string;
  state?: string;

  shiftId?: string;
  workingNights?: boolean;
  shiftStartAt?: string;
  hosRemainingMinutes?: number;
  minutesSinceLastCompletedLoad?: number;

  hitchId?: string;
  hitchStartDate?: string;
  hitchLastDate?: string;
  shiftsRemaining?: number;
  hitchStatus?: string;
  nextShiftStartDatetime?: string;
}

export interface MonthlyCost {
  id: number;
  companyId: string;
  driverId: string;
  payType: string;
  payAmountFixedCents: number | null;
  payAmountPercentage: number | null;
  insuranceCostCents: number;
  maintenanceCostCents: number;
  createdAt: string;
  driverName: string;
}

export interface InsertMonthlyCostRequest {
  driverId: string;
  payType: 'fixed' | 'percentage';
  payAmountFixedCents: number | null;
  payAmountPercentage: string | null;
  insuranceCostCents: number;
  maintenanceCostCents: number;
}

export interface InsertMonthlyCostResponse {
  id: number;
  driverId: string;
  payType: 'fixed' | 'percentage';
  payAmountFixedCents: number | null;
  payAmountPercentage: string | null;
  insuranceCostCents: number;
  maintenanceCostCents: number;
}

export interface DriverAverageRevenue {
  fixedCostCentsPerDay: number;
  averageFuelCostCentsPerDay: number;
  averageInsuranceCostCentsPerDay: number;
  averageMaintenanceCostCentsPerDay: number;
  averageTakeRateCentsPerDay: number;
}

export interface DriverAverageRevenueResponse {
  driver_average_revenue: DriverAverageRevenue;
}

export interface DriverAverageWastedTime {
  driverId: string;
  driverName: string;
  totalAverageTimeWastedMinutes: number;
  totalAverageTimeWastedFormatted: string;
  totalMissedRevenue: number;
  orders: {
    orderId: string;
    orderTimeWastedMinutes: number;
    orderTimeWastedFormatted: string;
  }[];
}

export interface DriverShiftDuration {
  driverId: string;
  driverName: string;
  totalMissedRevenue: number;
  shifts: {
    shiftId: string;
    orderId: string;
    shiftDuration: number;
    startTimestamp: string;
    endTimestamp: string;
  }[];
  totalAverageShiftDuration: number;
  totalAvgShiftDurationFormatted?: string;
  shiftDetailsFormatted?: string[];
}

export interface DriverLoss {
  driverId: string;
  driverName: string;
  driverPhone: number;
  totalRevenue: number;
  driverTier: {
    driverId: string;
    driverPhone: string;
    tierName: string;
    takeRate: number;
    lossRate: number;
  };
  totalMissedRevenue: number;
}

export interface DriverRejectedOrders {
  driverId: string;
  driverName: string;
  totalLoadsRejected: number;
  totalMissedRevenue: number;
  orderDetails: {
    shiftId: string;
    loadsRejected: number;
    totalShiftTime: number;
    orderId?: string;
    pickUpName?: string;
    dropOffName?: string;
    missedRevenue?: number;
  }[];
}

export interface DriverTier {
  driverId: string;
  driverPhone: string;
  tierName: string;
  takeRate: number;
  lossRate: number;
}

export interface LostRevenueByDriverResponse {
  driverId: string;
  driverName: string;
  driverTier: DriverTier;
  avgWastedTime: DriverAverageWastedTime;
  avgShiftDur: DriverShiftDuration;
  driverLosses: DriverLoss;
  rejectedLoads: DriverRejectedOrders;
  overallMissedRevenue: number;
}

export interface DriverDataBrief {
  id: string;
  phone: string;
  name: string;
}

export interface LoHiSandiLoadNeedingAdminApproval {
  id: string;
  uniqueId: string;
  driverId: number;
  driverName: string;
  driverPhoneNumber: string;
  shipperName: string;
  originSiteName: string;
  destinationSiteName: string;
}

export interface LoadsNeedingAdminAcceptanceApiResponse {
  loads: LoHiSandiLoadNeedingAdminApproval[];
  availableDrivers?: DriverDataBrief[];
}

@Injectable({
  providedIn: 'root',
})
export class LohiDashboardService {
  constructor(private httpclient: HttpClient) {}
  private readonly baseUrl = `${environment.api}/v2/admin/carrier_kpi`;
  private readonly loadsBaseUrl = `${environment.api}/v2/admin/loads`;

  private updateEventSubject$ = new Subject<void>();
  public updateEvent$ = this.updateEventSubject$.asObservable();

  private lostRevenueByDriver$ = this.httpclient
    .get<LostRevenueByDriverResponse[]>(`${this.baseUrl}/get_lost_revenue_by_driver`)
    .pipe(
      map((res) => res),
      shareReplay(1),
    );

  public getOnTimePerformance$(): Observable<OnTimePerformanceResponse> {
    return this.httpclient
      .get<OnTimePerformanceResponse>(`${this.baseUrl}/get_on_time_performance`)
      .pipe(map((res) => res));
  }

  public getRevenue$(): Observable<RevenueResponse> {
    return this.httpclient.get<RevenueResponse>(`${this.baseUrl}/get_revenue`).pipe(map((res) => res));
  }

  public getMissedRevenue$(): Observable<MissedRevenueResponse> {
    return this.httpclient.get<MissedRevenueResponse>(`${this.baseUrl}/get_missed_revenue`).pipe(map((res) => res));
  }

  public getAverageTurnRate$(): Observable<AverageTurnRateResponse> {
    return this.httpclient.get<AverageTurnRateResponse>(`${this.baseUrl}/get_turn_rate`).pipe(map((res) => res));
  }

  public getTotalRanLoads$(): Observable<number> {
    return this.httpclient
      .get<TotalRanLoadsResponse>(`${this.baseUrl}/get_total_ran_loads`)
      .pipe(map((res) => res.total_ran_loads));
  }

  public getTotalRejectedLoads$(): Observable<number> {
    return this.httpclient
      .get<TotalRejectedLoadsResponse>(`${this.baseUrl}/get_total_rejected_loads`)
      .pipe(map((res) => res.total_rejected_loads));
  }

  public getUnassignedLoads$(): Observable<number> {
    return this.httpclient
      .get<UnassignedLoadsResponse>(`${this.baseUrl}/get_unassigned_loads`)
      .pipe(map((res) => res.unassigned_loads));
  }

  public getLoadPerShift$(): Observable<number> {
    return this.httpclient
      .get<LoadPerShiftResponse>(`${this.baseUrl}/get_load_per_shift`)
      .pipe(map((res) => res.load_per_shift));
  }

  public getDocumentationTardiness$(): Observable<DocumentationTardiness[]> {
    return this.httpclient
      .get<DocumentationTardinessResponse>(`${this.baseUrl}/get_documentation_tardiness`)
      .pipe(map((res) => res.documentation_tardiness));
  }

  public getAppUsage$(): Observable<AppUsage[]> {
    return this.httpclient.get<AppUsageResponse>(`${this.baseUrl}/get_app_usage`).pipe(map((res) => res.app_usage));
  }

  public getDriverInProgress$(): Observable<DriverInProgress[]> {
    return this.httpclient
      .get<DriverInProgressResponse>(`${this.baseUrl}/get_driver_in_progress`)
      .pipe(map((res) => res.driver_in_progress));
  }

  public getDriverOnShift$(): Observable<DriverOnShift[]> {
    let params;
    params = new HttpParams().set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    return this.httpclient.get<DriverOnShift[]>(`${this.baseUrl}/get_driver_on_shift`, { params });
  }

  public getDriverOnHitch$(): Observable<DriverOnHitchResponse[]> {
    let params;
    params = new HttpParams().set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    return this.httpclient.get<DriverOnHitchResponse[]>(`${this.baseUrl}/get_driver_on_hitch`, { params });
  }

  public getDriverRevenue$(): Observable<DriverRevenue[]> {
    return this.httpclient.get<DriverRevenue[]>(`${this.baseUrl}/get_driver_revenue`).pipe(map((res) => res));
  }

  public insertMonthlyCosts$(request: InsertMonthlyCostRequest): Observable<InsertMonthlyCostResponse> {
    return this.httpclient
      .post<InsertMonthlyCostResponse>(`${this.baseUrl}/insert_monthly_costs`, request)
      .pipe(map((res) => res));
  }

  public getMonthlyCosts$(): Observable<MonthlyCost[]> {
    return this.httpclient.get<MonthlyCost[]>(`${this.baseUrl}/get_driver_operational_costs`).pipe(map((res) => res));
  }

  public updateMonthlyCosts$(costs: UpdateMonthlyCostRequest[]): Observable<UpdateMonthlyCostResponse> {
    return this.httpclient
      .post<UpdateMonthlyCostResponse>(`${this.baseUrl}/manage_driver_operational_costs`, costs)
      .pipe(
        map((res) => {
          this.updateEventSubject$.next();
          return res;
        }),
      );
  }

  public getDriverAverageCosts$(duration: number = 365): Observable<DriverAverageRevenue> {
    return this.httpclient
      .get<DriverAverageRevenueResponse>(`${this.baseUrl}/get_driver_average_costs?duration=${duration}`)
      .pipe(map((res) => res.driver_average_revenue));
  }

  public getLostRevenueByDriver$(): Observable<LostRevenueByDriverResponse[]> {
    return this.lostRevenueByDriver$;
  }

  public getCarrierHeatmap$(): Observable<any> {
    return this.httpclient.get<any>(`${this.baseUrl}/get_heatmap`).pipe(
      map((res) => {
        return res;
      }),
    );
  }

  public getLoadsNeedingAdminAcceptance$(): Observable<{
    loads: LoHiSandiLoadNeedingAdminApproval[];
    availableDrivers: DriverDataBrief[];
  }> {
    return this.httpclient.get<LoadsNeedingAdminAcceptanceApiResponse>(`${this.loadsBaseUrl}/need_admin_acceptance`).pipe(
      map((res) => ({
        loads: res?.loads ?? [],
        availableDrivers: res?.availableDrivers ?? [],
      })),
    );
  }

  public submitAdminApprovalResponse$(
    loadUniqueId: string,
    approved: boolean,
    swapToDriverId?: string | null,
  ): Observable<void> {
    const body: { approved: boolean; swapToDriverId?: string } = { approved };
    if (swapToDriverId != null && swapToDriverId !== '') {
      body.swapToDriverId = swapToDriverId;
    }
    return this.httpclient.post<void>(
      `${environment.api}/v2/load/${loadUniqueId}/submit_admin_load_approval_response`,
      body,
    );
  }
}
